import { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { AdjustmentsIcon, ExclamationIcon } from "@heroicons/react/outline";
import { format } from "date-fns";
import MaxWidthPage from "components/MaxWidthPage";
import Button from "components/Button";
import { CrawlerPageLayout } from "pages/crawler/components/CrawlerPageLayout";
import { RecentlyViewedProvider } from "providers/RecentlyViewed/RecentlyViewedProvider";
import { canUsePremium } from "utils/planUtils";
import CrawlerJobsTableControls from "pages/crawler/components/jobs/CrawlerJobsTableControls";
import CrawlerJobsTable from "pages/crawler/components/jobs/CrawlerJobsTable";
import CrawlerStatsCard from "pages/crawler/components/jobs/CrawlerStatsCard";
import { CrawlerDetails, CrawlerJob, CrawlerSummary, JobsSortBy } from "pages/crawler/components/jobs/types";
import scraperApi from "api";
import { useCrawlerJobsPolling } from "pages/crawler/hooks/useCrawlerJobsPolling";
import { updateJobStatus } from "pages/crawler/utils/jobStatusUtils";
import { isStatusRunning, getStatusLabel } from "pages/crawler/utils/crawlerStatusUtils";
import Toaster from "components/Toaster";
import { downloadJobData } from "pages/crawler/utils/crawlerDownloadUtils";
import { pickOutputFormat, pickDeviceType, pickRetry404, pickAutoparse } from "pages/crawler/utils/crawlerDisplayUtils";
import {
  makeTimestampDescComparator,
  makeStatusComparator,
  makeStringAscComparator,
} from "pages/crawler/utils/crawlerSortUtils";
import { CrawlerPageSkeleton } from "pages/crawler/components/jobs/StatsCardSkeleton";
import { FREE_PLAN_CUTOFF_DATE } from "pages/crawler/constants/crawlerConstants";
import DismissibleInfoBox from "components/DismissibleInfoBox";
import { useUser } from "routes/dataroutes/UserData";
import { useActiveSubscription } from "routes/data-routes/billing-data/active-subscription";


const compareByIdAsc = makeStringAscComparator("id");

const compareLastRunDesc = makeTimestampDescComparator(
  ["finishedAt"],
  compareByIdAsc
);

const compareCreatedDesc = makeTimestampDescComparator(
  ["createdAt"],
  compareByIdAsc
);

const compareStatusDesc = makeStatusComparator(
  (job: CrawlerJob) => job.status,
  compareLastRunDesc
);

const SORT_COMPARATORS: Record<JobsSortBy, (a: CrawlerJob, b: CrawlerJob) => number> = {
  lastRun: compareLastRunDesc,
  status: compareStatusDesc,
  created: compareCreatedDesc,
};

const calculateCrawlerStatus = (jobs: CrawlerJob[]) => {
  const runningJobs = jobs.filter((job) => isStatusRunning(job.status)).length;
  const scheduledJobs = jobs.filter((job) => getStatusLabel(job.status).toLowerCase() === "scheduled").length;

  if (runningJobs > 0) return `${runningJobs} running`;
  if (scheduledJobs > 0) return `${scheduledJobs} scheduled`;
  if (jobs.length > 0) return `Last: ${getStatusLabel(jobs[0].status)}`;
  return "Inactive";
};

const buildCrawlerDetails = (
  crawlerId: string,
  crawler: CrawlerSummary | null,
  jobs: CrawlerJob[]
): CrawlerDetails => {
  const firstJob = jobs[0];
  const totalJobs = jobs.length;
  const totalCreditsSpent = jobs.reduce((sum, job) => sum + (job.creditsSpent || 0), 0);
  const avgCostPerJob = totalJobs > 0 ? Math.round(totalCreditsSpent / totalJobs) : 0;

  return {
    id: crawlerId || crawler?.id || '',
    name: crawler?.name || firstJob?.crawlerName || "Unnamed Crawler",
    status: calculateCrawlerStatus(jobs),
    created: format(new Date(crawler?.createdAt || firstJob?.createdAt || new Date()), "yyyy-MM-dd HH:mm"),
    avgCostPerJob,
    totalCreditsSpent,
    outputFormat: pickOutputFormat(crawler?.apiParams, firstJob?.apiParams, firstJob?.additionalData?.outputFormat),
    autoparse: pickAutoparse(crawler?.apiParams, firstJob?.apiParams, firstJob?.additionalData?.autoparse),
    deviceType: pickDeviceType(crawler?.apiParams, firstJob?.apiParams, firstJob?.additionalData?.deviceType),
    retry404: pickRetry404(crawler?.apiParams, firstJob?.apiParams, firstJob?.additionalData?.retry404),
    totalJobs,
  };
};

const ViewAllCrawlerJobsPage = () => {
  const { id: crawlerId } = useParams();
  const navigate = useNavigate();
  const user = useUser();

  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState<JobsSortBy>("lastRun");
  // Local jobs state for optimistic updates (e.g. cancel)
  const [jobs, setJobs] = useState<CrawlerJob[]>([]);

  const isOutOfCredits = !user || user.creditsRemaining <= 0;

  const {
    jobs: polledJobs,
    currentCrawler,
    isLoading: initialLoading,
    hasNextPage,
    loadMoreJobs,
  } = useCrawlerJobsPolling({
    crawlerId,
    enabled: !!crawlerId,
  });

  // Sync polled jobs to local state (local state allows optimistic updates)
  useEffect(() => {
    if (polledJobs.length > 0 || !initialLoading) {
      setJobs(polledJobs);
    }
  }, [polledJobs, initialLoading]);

  const { activeSubscription } = useActiveSubscription();
  const isFreePlan = activeSubscription && !canUsePremium(activeSubscription?.planSlug);

  const showDisabledDuePlanDowngradeBanner = isFreePlan && ((currentCrawler?.traits ?? 0) &  8) !== 0;
  const showGracePeriodBanner              = isFreePlan && ((currentCrawler?.traits ?? 0) & 16) !== 0;

  // Derive crawler details from current data
  const crawlerDetails = useMemo(
    () => buildCrawlerDetails(crawlerId || '', currentCrawler, jobs),
    [crawlerId, currentCrawler, jobs]
  );

  const handleDownloadJob = async (jobId: string) => {
    const outputFormat = crawlerDetails?.outputFormat?.toLowerCase() || 'json';
    await downloadJobData(jobId, outputFormat);
  };

  const handleJobAction = async (action: string, jobId: string) => {
    if (action === "view") {
      navigate(`/crawlers/${crawlerId}/jobs/${jobId}`);
    } else if (action === "download") {
      await handleDownloadJob(jobId);
    } else if (action === "cancel") {
      const originalJob = jobs.find(job => job.id === jobId);
      const originalStatus = originalJob?.status || 'Running';

      try {
        setJobs(prevJobs => updateJobStatus(prevJobs, jobId, 'Cancelled'));

        const response = await scraperApi.crawlers.cancelCrawlerJob(jobId);

        if (response.success) {
          Toaster.success('Job cancelled successfully');
        } else {
          setJobs(prevJobs => updateJobStatus(prevJobs, jobId, originalStatus));
          throw new Error(response.message || 'Failed to cancel job');
        }
      } catch (error: any) {
        console.error('Failed to cancel job:', error);
        const errorMessage = error.response?.data?.message || error.message || 'Failed to cancel job';
        Toaster.error(errorMessage);
        setJobs(prevJobs => updateJobStatus(prevJobs, jobId, originalStatus));
      }
    }
  };

  // Filter and sort jobs
  const sortedJobs = useMemo(() => {
    const searchTerm = searchQuery.toLowerCase();
    const filtered = jobs.filter((job) =>
      (job.startUrl || '').toLowerCase().includes(searchTerm) ||
      (job.crawlerName || '').toLowerCase().includes(searchTerm) ||
      (job.id || '').toLowerCase().includes(searchTerm)
    );
    const comparator = SORT_COMPARATORS[sortBy] ?? compareLastRunDesc;
    return [...filtered].sort((a, b) => comparator(a, b));
  }, [jobs, searchQuery, sortBy]);

  if (initialLoading) {
    return (
      <MaxWidthPage fullWidth>
        <CrawlerPageSkeleton />
      </MaxWidthPage>
    );
  }

  return (
    <MaxWidthPage fullWidth>
      <RecentlyViewedProvider>
        <CrawlerPageLayout
          crawlerName={crawlerDetails?.name}
          readonly={true}
          titleButtons={
            <Button
              text="EDIT CRAWLER"
              icon={{ Icon: AdjustmentsIcon }}
              href={crawlerId ? `/crawlers/${crawlerId}/edit` : undefined}
              disabled={!crawlerId}
              className="bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 rounded px-4 py-2"
            />
          }
        >
          {showGracePeriodBanner && (
            <div>
              <DismissibleInfoBox
                id={`crawler-grace-period-banner-${crawlerId}`}
                icon={<ExclamationIcon className="w-5 h-5 flex-shrink-0 text-amber-500" />}
              >
                <p>
                  This job was created during the Crawler beta and is running under legacy settings. Free plan restrictions will apply starting{" "}
                  <span className="font-semibold">{FREE_PLAN_CUTOFF_DATE}</span>.{", "}
                  upgrade before then to keep scheduled jobs running without interruption.
                </p>
              </DismissibleInfoBox>
              <hr className="border-t border-gray-200 mt-4" />
            </div>
          )}
          {!showGracePeriodBanner && showDisabledDuePlanDowngradeBanner && (
            <div>
              <DismissibleInfoBox
                id={`crawler-disabled-due-plan-dowgrade-${crawlerId}`}
                icon={<ExclamationIcon className="w-5 h-5 flex-shrink-0 text-amber-500" />}
              >
                <p>
                  This scheduled job is disabled because scheduled crawling is not available on your current plan. Upgrade to re-enable scheduling.
                </p>
              </DismissibleInfoBox>
              <hr className="border-t border-gray-200 mt-4" />
            </div>
          )}
          <div className="mb-6 min-h-[60vh]">
            <CrawlerStatsCard details={crawlerDetails} />

            {isOutOfCredits && (
              <div className="mt-4 mb-4 p-3 bg-yellow-50 rounded-lg border border-yellow-300">
                <div className="flex items-center gap-2">
                  <ExclamationIcon className="w-5 h-5 flex-shrink-0 text-yellow-600" />
                  <span className="text-sm font-medium text-yellow-800">You are out of credits. Your crawls may stop or fail until you add more credits.</span>
                </div>
              </div>
            )}

            {currentCrawler?.status?.state === 'running' && (
              <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
                  <span className="text-sm font-medium text-blue-800">Crawler is currently running</span>
                </div>
              </div>
            )}

            <CrawlerJobsTableControls
              totalCount={jobs.length}
              shownCount={sortedJobs.length}
              sortBy={sortBy}
              onSortChange={setSortBy}
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
            />

            <CrawlerJobsTable
              jobs={sortedJobs}
              outputFormat={crawlerDetails?.outputFormat}
              onRowClick={(job) => navigate(`/crawlers/${crawlerId}/jobs/${job.id}`)}
              onJobAction={handleJobAction}
            />

            {hasNextPage && (
              <div className="flex justify-end mt-2">
                <Button
                  onClick={loadMoreJobs}
                  className="button button-primary rounded-md"
                  text="LOAD MORE"
                />
              </div>
            )}
          </div>
        </CrawlerPageLayout>
      </RecentlyViewedProvider>
    </MaxWidthPage>
  );
};

export default ViewAllCrawlerJobsPage;
